/**
 * WebRTC session manager (V3b — SM-9 client media plane, D-005).
 *
 * One RtcSession per live call: RTCPeerConnection with STUN + short-lived
 * TURN credentials from GET /calls/config (P2P-first, TURN fallback), local
 * mic/cam capture, trickle ICE over the authenticated WS (`call.ice`), and
 * SDP offer/answer over `call.sdp` (the caller offers once CONNECTING; the
 * answer flips the server state to ACTIVE). Media never touches the app
 * server. Honest fallback: when the config carries no TURN server the
 * session reports it so the UI shows ©call.turn_unavailable instead of
 * failing silently behind strict NATs.
 */
import { api } from "../../lib/api.js";
import { ws, wsCommand } from "../../lib/ws.js";
import type { CallKind } from "./call-store.js";

export interface RtcCallbacks {
  onLocalStream(stream: MediaStream): void;
  onRemoteStream(stream: MediaStream): void;
  /** Terminal media/negotiation failure — the screen offers retry/hangup. */
  onFailure(kind: "permissions" | "connection"): void;
}

interface IceServerEntry {
  urls: string[];
  username?: string;
  credential?: string;
}

export class RtcSession {
  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private offs: Array<() => void> = [];
  private closed = false;
  /** Trickle-ICE received before the remote description — addIceCandidate
   *  would throw (and be lost) pre-SDP; queued and flushed after it applies. */
  private pendingIce: RTCIceCandidateInit[] = [];

  constructor(
    readonly callId: string,
    readonly kind: CallKind,
    readonly role: "caller" | "callee",
    private readonly cb: RtcCallbacks,
  ) {}

  /** Returns whether a TURN relay is configured (honest-fallback signal). */
  async start(): Promise<{ turnAvailable: boolean }> {
    const cfg = (await api.get("/calls/config")) as {
      ice_servers?: IceServerEntry[];
    };
    const iceServers = cfg.ice_servers ?? [];
    const turnAvailable = iceServers.some((s) => s.urls.some((u) => u.startsWith("turn")));

    let local: MediaStream;
    try {
      local = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: this.kind === "video",
      });
    } catch {
      this.cb.onFailure("permissions");
      return { turnAvailable };
    }
    if (this.closed) {
      local.getTracks().forEach((t) => t.stop());
      return { turnAvailable };
    }
    this.local = local;
    this.cb.onLocalStream(local);

    const pc = new RTCPeerConnection({ iceServers: iceServers as RTCIceServer[] });
    this.pc = pc;
    local.getTracks().forEach((t) => pc.addTrack(t, local));

    pc.ontrack = (ev) => {
      const stream = ev.streams[0];
      if (stream) this.cb.onRemoteStream(stream);
    };
    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        void wsCommand("call.ice", { call_id: this.callId, candidate: ev.candidate.toJSON() }).catch(
          () => {},
        );
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed") this.cb.onFailure("connection");
    };

    // Peer signaling → this pc. The server relays only for live calls we are
    // party to; payloads remain re-render hints, the pc is the only consumer.
    this.offs.push(
      ws.on("call.sdp", (p) => {
        if (p.call_id !== this.callId) return;
        void this.onRemoteSdp(p.desc);
      }),
    );
    this.offs.push(
      ws.on("call.ice", (p) => {
        if (p.call_id !== this.callId || !p.candidate) return;
        const candidate = p.candidate as RTCIceCandidateInit;
        if (!pc.remoteDescription) {
          this.pendingIce.push(candidate); // pre-SDP: hold, flush after it applies
          return;
        }
        void pc.addIceCandidate(candidate).catch(() => {});
      }),
    );

    if (this.role === "caller") {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await wsCommand("call.sdp", {
        call_id: this.callId,
        desc: { type: "offer", sdp: offer.sdp ?? "" },
      }).catch(() => this.cb.onFailure("connection"));
    } else {
      // Late-subscriber recovery: the caller may have emitted the offer
      // BEFORE our `call.sdp` subscription above existed (pushes are
      // fire-and-forget). Ask the server for the buffered current offer —
      // it comes back as a normal `call.sdp` push; duplicates are dropped
      // by the signaling-state guard in onRemoteSdp.
      void wsCommand("call.sdp_request", { call_id: this.callId }).catch(() => {});
    }
    return { turnAvailable };
  }

  private async onRemoteSdp(desc: { type: "offer" | "answer"; sdp: string }): Promise<void> {
    const pc = this.pc;
    if (!pc) return;
    try {
      // Duplicate/stale-frame guards (the buffered-offer re-delivery can race
      // the live relay): an offer only applies while we have no remote
      // description yet; an answer only while our own offer is outstanding.
      if (desc.type === "offer" && (pc.remoteDescription || pc.signalingState !== "stable")) return;
      if (desc.type === "answer" && pc.signalingState !== "have-local-offer") return;
      await pc.setRemoteDescription({ type: desc.type, sdp: desc.sdp });
      // Flush candidates that arrived before the remote description.
      for (const candidate of this.pendingIce.splice(0)) {
        void pc.addIceCandidate(candidate).catch(() => {});
      }
      if (desc.type === "offer") {
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        await wsCommand("call.sdp", {
          call_id: this.callId,
          desc: { type: "answer", sdp: answer.sdp ?? "" },
        });
      }
    } catch {
      this.cb.onFailure("connection");
    }
  }

  setMuted(muted: boolean): void {
    this.local?.getAudioTracks().forEach((t) => {
      t.enabled = !muted;
    });
  }

  setCameraOff(off: boolean): void {
    this.local?.getVideoTracks().forEach((t) => {
      t.enabled = !off;
    });
  }

  close(): void {
    this.closed = true;
    this.offs.forEach((off) => off());
    this.offs = [];
    this.local?.getTracks().forEach((t) => t.stop());
    this.local = null;
    try {
      this.pc?.close();
    } catch {
      /* already closed */
    }
    this.pc = null;
  }
}
